import React, { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { MdAdd, MdClose, MdDelete, MdEdit, MdRefresh, MdSave, MdSearch, MdSettings } from 'react-icons/md';
import { Settings } from '../@backend';
import useApi from '../hooks/useApi';
import { userState } from '../store';
import { Menu } from '../ui/layout/Menu';
import { FooterCmp } from '../ui/layout/Footer';
import { PageHeader } from '../ui/ds/PageHeader';
import { Card } from '../ui/ds/Card';
import { Btn } from '../ui/ds/Btn';
import { Badge } from '../ui/ds/Badge';
import { Input } from '../ui/ds/Input';
import { AdminSubmenu } from './AdminSubmenu';

type SettingForm = {
    key: string;
    value: string;
};

const emptyForm: SettingForm = {
    key: '',
    value: '',
};

function SettingValueField({
    value,
    onChange,
}: {
    value: string;
    onChange: (value: string) => void;
}) {
    return (
        <div className="flex flex-col gap-1.5 mb-4">
            <p className="tb-label">Hodnota</p>
            <textarea
                rows={5}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder="Hodnota nastavení"
                className="w-full bg-bg-input border-2 border-border-neutral rounded-input px-4 py-3 text-[15px] font-semibold text-text-primary font-nunito placeholder:text-text-muted focus:border-brand-blue focus:outline-none transition-colors resize-y min-h-[120px]"
                required
            />
        </div>
    );
}

export const AdminSettings = () => {
    const userSnap = useSnapshot(userState);
    const { backend } = useApi();

    const [settings, setSettings] = useState<Settings[]>([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [createForm, setCreateForm] = useState<SettingForm>(emptyForm);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const [workingKey, setWorkingKey] = useState<string | 'new' | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const filteredSettings = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return settings;
        return settings.filter(setting =>
            [setting.key, setting.value].join(' ').toLowerCase().includes(q)
        );
    }, [query, settings]);

    const clearFeedback = () => {
        setMessage(null);
        setError(null);
    };

    const showError = (e: any, fallback: string) => {
        setError(e?.body?.message ?? e?.message ?? fallback);
        setMessage(null);
    };

    const loadSettings = async (resetFeedback = true) => {
        setLoading(true);
        if (resetFeedback) clearFeedback();
        try {
            const loaded = await backend.settingsController.settingsControllerAdminListSettings();
            setSettings(loaded ?? []);
        } catch (e: any) {
            showError(e, 'Nastavení se nepodařilo načíst.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userSnap.isLoggedIn && userSnap.userProfile.role === 'Admin') {
            loadSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSnap.isLoggedIn, userSnap.userProfile.role]);

    const createSetting = async (e: React.FormEvent) => {
        e.preventDefault();
        const key = createForm.key.trim();
        if (!key) return;

        setWorkingKey('new');
        clearFeedback();
        try {
            await backend.settingsController.settingsControllerAdminCreateSetting({
                key,
                value: createForm.value,
            });
            setCreateForm(emptyForm);
            setMessage('Nastavení bylo vytvořeno.');
            await loadSettings(false);
        } catch (e: any) {
            showError(e, 'Nastavení se nepodařilo vytvořit.');
        } finally {
            setWorkingKey(null);
        }
    };

    const startEdit = (setting: Settings) => {
        setEditingKey(setting.key);
        setEditValue(setting.value);
        clearFeedback();
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditValue('');
    };

    const saveSetting = async (key: string) => {
        setWorkingKey(key);
        clearFeedback();
        try {
            await backend.settingsController.settingsControllerAdminUpdateSetting(key, {
                value: editValue,
            });
            setMessage('Nastavení bylo uloženo.');
            cancelEdit();
            await loadSettings(false);
        } catch (e: any) {
            showError(e, 'Nastavení se nepodařilo uložit.');
        } finally {
            setWorkingKey(null);
        }
    };

    const deleteSetting = async (setting: Settings) => {
        if (!window.confirm(`Opravdu smazat nastavení ${setting.key}? Tuto akci nelze vrátit.`)) return;

        setWorkingKey(setting.key);
        clearFeedback();
        try {
            await backend.settingsController.settingsControllerAdminDeleteSetting(setting.key);
            setMessage('Nastavení bylo smazáno.');
            await loadSettings(false);
        } catch (e: any) {
            showError(e, 'Nastavení se nepodařilo smazat.');
        } finally {
            setWorkingKey(null);
        }
    };

    if (!userSnap.isLoggedIn) {
        return (
            <>
                <Menu />
                <PageHeader title="Administrace" subtitle="Správa uživatelů a systému" />
                <div className="max-w-xl mx-auto px-4 py-16 text-center">
                    <span className="text-[56px]">🔒</span>
                    <p className="tb-h2 text-text-primary mt-4 mb-2">Pouze pro administrátory</p>
                    <p className="tb-body text-text-muted">Pro pokračování se přihlaste administrátorským účtem.</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    if (userSnap.userProfile.role !== 'Admin') {
        return (
            <>
                <Menu />
                <PageHeader title="Administrace" subtitle="Správa uživatelů a systému" />
                <div className="max-w-xl mx-auto px-4 py-16 text-center">
                    <span className="text-[56px]">🚫</span>
                    <p className="tb-h2 text-text-primary mt-4 mb-2">Nemáte oprávnění</p>
                    <p className="tb-body text-text-muted">Tato stránka je dostupná pouze administrátorům.</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    return (
        <>
            <Menu />
            <PageHeader
                title="Administrace"
                subtitle={`${settings.length} nastavení`}
                right={
                    <button
                        type="button"
                        onClick={() => loadSettings()}
                        disabled={loading}
                        title="Obnovit nastavení"
                        className="w-10 h-10 flex items-center justify-center rounded-content bg-white/15 hover:bg-white/25 transition-colors text-white disabled:opacity-50"
                    >
                        <MdRefresh className={`text-[22px] ${loading ? 'animate-spin' : ''}`} />
                    </button>
                }
            />
            <AdminSubmenu />

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col gap-6">
                {(message || error) && (
                    <div className={[
                        'flex items-center gap-3 rounded-content px-4 py-3',
                        error
                            ? 'bg-semantic-danger/10 border border-semantic-danger/20'
                            : 'bg-semantic-success/10 border border-semantic-success/20',
                    ].join(' ')}>
                        <Badge variant={error ? 'danger' : 'success'}>
                            {error ? 'Chyba' : 'OK'}
                        </Badge>
                        <span className="tb-body text-text-primary">{error ?? message}</span>
                        <button
                            onClick={() => { setError(null); setMessage(null); }}
                            className="ml-auto text-text-muted hover:text-text-primary transition-colors"
                        >
                            <MdClose className="text-[18px]" />
                        </button>
                    </div>
                )}

                <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-6 items-start">
                    <Card>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-11 h-11 rounded-content bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                                <MdAdd className="text-[24px]" />
                            </div>
                            <div>
                                <p className="tb-h3 text-text-primary">Nové nastavení</p>
                                <p className="tb-caption">Klíč a hodnota globálního nastavení.</p>
                            </div>
                        </div>

                        <form onSubmit={createSetting}>
                            <Input
                                label="Klíč"
                                value={createForm.key}
                                onChange={e => setCreateForm({ ...createForm, key: e.target.value })}
                                placeholder="activeTournamentId"
                                required
                            />
                            <SettingValueField
                                value={createForm.value}
                                onChange={value => setCreateForm({ ...createForm, value })}
                            />
                            <Btn
                                type="submit"
                                variant="primary"
                                size="md"
                                fullWidth
                                icon={<MdAdd />}
                                disabled={workingKey !== null || !createForm.key.trim()}
                            >
                                {workingKey === 'new' ? 'Vytvářím…' : 'Vytvořit nastavení'}
                            </Btn>
                        </form>
                    </Card>

                    <Card padding="none">
                        <div className="px-5 py-4 border-b border-border-neutral flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-bg-app rounded-t-content">
                            <div>
                                <p className="tb-h3 text-text-primary">Globální nastavení</p>
                                <p className="tb-caption">Zobrazeno {filteredSettings.length} z {settings.length}</p>
                            </div>
                            <div className="relative w-full sm:w-80">
                                <MdSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[20px] text-text-muted" />
                                <input
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    placeholder="Hledat nastavení…"
                                    className="w-full h-[44px] bg-bg-input border-2 border-border-neutral rounded-input pl-11 pr-4 text-[15px] font-semibold text-text-primary font-nunito placeholder:text-text-muted focus:border-brand-blue focus:outline-none transition-colors"
                                />
                            </div>
                        </div>

                        {loading && (
                            <div className="flex items-center justify-center py-16">
                                <MdRefresh className="text-[32px] text-brand-blue animate-spin" />
                            </div>
                        )}

                        {!loading && filteredSettings.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <MdSettings className="text-[52px] text-text-muted/40" />
                                <p className="tb-h3 text-text-primary">Žádná nastavení</p>
                                <p className="tb-caption">Zkuste jiný dotaz nebo vytvořte nové nastavení.</p>
                            </div>
                        )}

                        {!loading && filteredSettings.map(setting => {
                            const isEditing = editingKey === setting.key;
                            return (
                                <div
                                    key={setting.key}
                                    className="flex flex-col lg:grid lg:grid-cols-[260px_minmax(0,1fr)_220px] items-start lg:items-center gap-3 px-5 py-4 border-b border-divider-row last:border-b-0 hover:bg-bg-app transition-colors"
                                >
                                    <div className="min-w-0">
                                        <p className="tb-h4 text-text-primary leading-tight break-all">{setting.key}</p>
                                        <p className="tb-caption">Klíč nastavení</p>
                                    </div>

                                    <div className="w-full min-w-0">
                                        {isEditing ? (
                                            <SettingValueField value={editValue} onChange={setEditValue} />
                                        ) : (
                                            <pre className="max-h-[120px] overflow-auto scrollbar-thin bg-bg-inputSoft rounded-input border border-border-neutral px-3 py-2 text-[12px] font-mono text-text-primary whitespace-pre-wrap break-words">
                                                {setting.value}
                                            </pre>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {isEditing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => saveSetting(setting.key)}
                                                    disabled={workingKey === setting.key}
                                                    title="Uložit nastavení"
                                                    className="flex items-center gap-1 h-8 px-3 bg-semantic-success text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                                >
                                                    <MdSave className="text-[15px]" />
                                                    {workingKey === setting.key ? '…' : 'Uložit'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEdit}
                                                    title="Zrušit úpravy"
                                                    className="flex items-center gap-1 h-8 px-3 bg-text-primary text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity"
                                                >
                                                    <MdClose className="text-[15px]" />
                                                    Zrušit
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => startEdit(setting)}
                                                    disabled={workingKey !== null}
                                                    title="Upravit nastavení"
                                                    className="flex items-center gap-1 h-8 px-3 bg-brand-blue text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                                >
                                                    <MdEdit className="text-[15px]" />
                                                    Upravit
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => deleteSetting(setting)}
                                                    disabled={workingKey !== null}
                                                    title="Smazat nastavení"
                                                    className="flex items-center gap-1 h-8 px-3 bg-semantic-danger text-white rounded-btn text-[12px] font-bold font-nunito hover:opacity-90 transition-opacity disabled:opacity-50"
                                                >
                                                    <MdDelete className="text-[15px]" />
                                                    Smazat
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </Card>
                </div>
            </main>

            <FooterCmp />
        </>
    );
};
